import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '../locales';

const LanguageContext = createContext();

export const languages = {
  nl: { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  fr: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  de: { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check if user explicitly chose a language (not just from URL navigation)
    const userChose = localStorage.getItem('netblitz-language-user-choice') === 'true';
    const saved = localStorage.getItem('netblitz-language');
    
    // If user explicitly chose a language, respect that choice
    if (userChose && saved && languages[saved]) {
      return saved;
    }
    
    // Otherwise, detect browser language
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang) {
      // Get the primary language code (e.g., 'nl-NL' -> 'nl', 'en-US' -> 'en')
      const primaryLang = browserLang.split('-')[0].toLowerCase();
      
      // Check if we support this language
      if (languages[primaryLang]) {
        return primaryLang;
      }
    }
    
    // Default to Dutch
    return 'nl';
  });

  // Wrapper function that marks language as user-chosen when called from UI
  const setLanguage = useCallback((newLang, isUserChoice = false) => {
    setLanguageState(newLang);
    if (isUserChoice) {
      localStorage.setItem('netblitz-language-user-choice', 'true');
    }
  }, []);
  
  // CMS content state
  const [cmsContent, setCmsContent] = useState({});
  const [cmsLoaded, setCmsLoaded] = useState(false);

  useEffect(() => {
    localStorage.setItem('netblitz-language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Fetch CMS content on mount
  useEffect(() => {
    const fetchCmsContent = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`);
        if (response.ok) {
          const data = await response.json();
          
          // Transform to nested object
          const contentMap = {};
          data.forEach(item => {
            if (!contentMap[item.page]) {
              contentMap[item.page] = {};
            }
            if (!contentMap[item.page][item.section]) {
              contentMap[item.page][item.section] = {};
            }
            contentMap[item.page][item.section][item.key] = {
              nl: item.content_nl,
              en: item.content_en,
              fr: item.content_fr,
              de: item.content_de,
            };
          });
          
          setCmsContent(contentMap);
          setCmsLoaded(true);
        }
      } catch (err) {
        // CMS content not available, will use defaults
        setCmsLoaded(true);
      }
    };
    
    fetchCmsContent();
  }, []);

  // Get CMS content by path (page.section.key)
  const getCmsContent = useCallback((path) => {
    const parts = path.split('.');
    let value = cmsContent;
    
    for (const part of parts) {
      if (value && value[part]) {
        value = value[part];
      } else {
        return null;
      }
    }
    
    if (value && typeof value === 'object' && value[language]) {
      return value[language];
    }
    
    return null;
  }, [cmsContent, language]);

  const t = useCallback((key) => {
    // First try to get from CMS (for keys that match CMS structure)
    // Map translation keys to CMS paths
    const cmsKeyMap = {
      'hero.badge': 'home.hero.badge',
      'hero.title1': 'home.hero.title1',
      'hero.title2': 'home.hero.title2',
      'hero.title3': 'home.hero.title3',
      'hero.title4': 'home.hero.title4',
      'hero.description': 'home.hero.description',
      'hero.benefit1': 'home.hero.benefit1',
      'hero.benefit2': 'home.hero.benefit2',
      'hero.benefit3': 'home.hero.benefit3',
      'hero.ctaPrimary': 'home.hero.ctaPrimary',
      'hero.ctaSecondary': 'home.hero.ctaSecondary',
      'services.badge': 'home.services.badge',
      'services.title': 'home.services.title',
      'services.titleHighlight': 'home.services.titleHighlight',
      'services.titleEnd': 'home.services.titleEnd',
      'services.description': 'home.services.description',
      'cta.title': 'home.cta.title',
      'cta.description': 'home.cta.description',
      'nav.home': 'global.nav.home',
      'nav.services': 'global.nav.services',
      'nav.portfolio': 'global.nav.portfolio',
      'nav.pricing': 'global.nav.pricing',
      'nav.faq': 'global.nav.faq',
      'nav.contact': 'global.nav.contact',
      'nav.cta': 'global.nav.cta',
      'footer.description': 'global.footer.description',
      'footer.copyright': 'global.footer.copyright',
    };
    
    const cmsPath = cmsKeyMap[key];
    if (cmsPath && cmsLoaded) {
      const cmsValue = getCmsContent(cmsPath);
      if (cmsValue) {
        return cmsValue;
      }
    }
    
    // Fallback to JSON translations
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // Fallback to Dutch
        value = translations['nl'];
        for (const fallbackKey of keys) {
          value = value?.[fallbackKey];
        }
        break;
      }
    }
    
    return value || key;
  }, [language, cmsLoaded, getCmsContent]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages, cmsContent, cmsLoaded, getCmsContent }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default translations;
